// src/components/ui/Table/TableCell.tsx
import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumn } from './Table';
import {
  formatNumber,
  formatPercent,
  formatCurrency,
  formatSmartDate,
  truncateText
} from '@/utils/Formatters';
import styles from './TableCell.module.css';

interface TableCellProps<T> {
  column: TableColumn<T>;
  item: T;
  index: number;
  width?: number;
  compactMode?: boolean; // Новый параметр для компактного режима
}

export function TableCell<T>({
  column,
  item,
  index,
  width,
  compactMode = false
}: TableCellProps<T>) {
  // Используем хук для переводов
  const { t } = useTranslation('ui');

  // Вспомогательная функция для форматирования контента ячейки на основе типа данных
  const formatCellContent = (
    value: any, 
    dataType?: string, 
    maxLength?: number
  ): React.ReactNode => {
    if (value === undefined || value === null || value === '') {
      return <span className={styles.noData}>{t('common.noData')}</span>;
    }
    
    // Форматирование в зависимости от типа данных
    if (dataType === 'date') {
      return formatSmartDate(String(value), false);
    } else if (dataType === 'datetime') {
      return formatSmartDate(String(value), true);
    } else if (dataType === 'currency') {
      return formatCurrency(value);
    } else if (dataType === 'percent') {
      return formatPercent(value);
    } else if (dataType === 'number') {
      return formatNumber(value);
    }
    
    // Если тип данных не указан или это текст
    const textStr = String(value);
    
    // В компактном режиме используем меньшее значение для maxLength
    const effectiveMaxLength = compactMode && !maxLength 
      ? 20  // Для компактного режима без заданного maxLength
      : compactMode && maxLength 
        ? Math.min(maxLength, 30)  // Для компактного режима с заданным maxLength
        : maxLength;  // Стандартное значение maxLength
    
    // Обрезаем текст, если указана максимальная длина
    if (effectiveMaxLength && textStr.length > effectiveMaxLength) {
      return truncateText(textStr, effectiveMaxLength);
    }
    
    return textStr;
  };

  // Определяем класс выравнивания ячейки
  const getAlignClass = () => {
    switch (column.textAlign) {
      case 'center': return styles.cellCenter;
      case 'right': return styles.cellRight;
      default: return styles.cellLeft;
    }
  };

  // Определяем класс для переноса текста
  const getWrapClass = () => {
    if (compactMode || column.truncate) {
      return styles.cellTruncate;
    } else if (column.wrap) {
      return styles.cellWrap;
    } else {
      return styles.cellNowrap;
    }
  };

  // Определяем класс для типа данных
  const getDataTypeClass = () => {
    if (column.dataType === 'number') {
      return styles.cellNumber;
    } else if (column.dataType === 'currency') {
      return styles.cellCurrency;
    } else if (column.dataType === 'percent') {
      return styles.cellPercent;
    } else if (column.dataType === 'date' || column.dataType === 'datetime') {
      return styles.cellDate;
    }
    return styles.cellText;
  };

  // Определяем класс для компактного режима
  const compactClass = compactMode ? styles.compact : '';

  // Получаем значение ячейки
  const rawValue = (item as any)[column.key];
  
  // Получаем содержимое ячейки
  let content;
  if (column.render) {
    // Если есть кастомный рендер, используем его
    content = column.render(item, index);
  } else {
    // Иначе форматируем данные на основе их типа
    content = formatCellContent(rawValue, column.dataType, column.maxLength);
  }

  // Стили для ячейки
  const cellStyle: CSSProperties = {};
  
  // Применяем ширину ячейки, если она указана
  if (width) {
    cellStyle.width = `${width}px`;
    cellStyle.minWidth = `${width}px`;
    cellStyle.maxWidth = `${width}px`;
  }

  // Определяем классы для содержимого ячейки
  const contentClass = compactMode || column.truncate 
    ? styles.truncatedContent 
    : column.wrap 
      ? styles.wrappedContent 
      : '';

  // Получаем значение для атрибута title
  const titleValue = typeof rawValue === 'string' || typeof rawValue === 'number' ? 
    String(rawValue) : undefined;

  return (
    <td 
      className={`${styles.cell} ${getAlignClass()} ${getWrapClass()} ${getDataTypeClass()} ${compactClass}`} 
      style={cellStyle}
    >
      <div 
        className={`${styles.cellContent} ${contentClass}`}
        title={titleValue}
      >
        {content}
      </div>
    </td>
  );
}

export default TableCell;